import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios from "@/utils/managedAxios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Dna, 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  AlertCircle,
  ChevronRight,
  Lock,
  Sparkles,
  BarChart3,
  RefreshCw,
  History,
  Target,
  Zap,
  ArrowLeft,
  Flame
} from "lucide-react";
import { API_BASE } from "@/App";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/auth/AuthContext";

/**
 * TGPSC PYQ Genome Page
 * Matches UPSC PYQGenomePage UI pattern with exam_type=TGPSC
 */
const TGPSCPYQGenomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [genomeData, setGenomeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const { toast } = useToast();

  const isPro = user?.role === "PRO";

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(`${API_BASE}/prelims/genome/subjects?exam_type=TGPSC`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data.subjects || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const fetchGenomeAnalysis = async (subjectId) => {
    setLoading(true);
    setSelectedSubject(subjectId);
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(`${API_BASE}/prelims/genome/rotation/${subjectId}?exam_type=TGPSC`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGenomeData(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch genome analysis",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getProbabilityColor = (probability) => {
    if (probability >= 70) return "text-red-600 bg-red-50 border-red-200";
    if (probability >= 50) return "text-amber-600 bg-amber-50 border-amber-200";
    return "text-green-600 bg-green-50 border-green-200";
  };

  const getProbabilityBadge = (probability) => {
    if (probability >= 70) return { bg: "bg-red-500", text: "HIGH" };
    if (probability >= 50) return { bg: "bg-amber-500", text: "MEDIUM" };
    return { bg: "bg-green-500", text: "LOW" };
  };

  const TopicCard = ({ topic, type }) => {
    const probBadge = getProbabilityBadge(topic.probability_2026);
    
    return (
      <div className={`p-4 rounded-lg border ${getProbabilityColor(topic.probability_2026)} transition-all hover:shadow-md`}>
        <div className="flex items-start justify-between mb-2">
          <h4 className="font-semibold text-slate-800">{topic.topic}</h4>
          <Badge className={`${probBadge.bg} text-white text-xs`}>
            {topic.probability_2026}%
          </Badge>
        </div>
        
        <div className="grid grid-cols-3 gap-2 text-xs mb-3">
          <div className="flex items-center gap-1">
            <History className="w-3 h-3" />
            <span>Last: {topic.last_asked}</span>
          </div>
          <div className="flex items-center gap-1">
            <RefreshCw className="w-3 h-3" />
            <span>Cycle: {topic.cycle_years}yr</span>
          </div>
          <div className="flex items-center gap-1">
            <BarChart3 className="w-3 h-3" />
            <span>Asked: {topic.times_asked || topic.times_asked_15yr || 0}x</span>
          </div>
        </div>
        
        <p className="text-xs text-slate-600 italic">
          {topic.reason}
        </p>
      </div>
    );
  };

  // Group subjects by type (state-specific vs general)
  const stateSpecificSubjects = subjects.filter(s => s.state_specific);
  const generalSubjects = subjects.filter(s => !s.state_specific);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-pink-50 to-slate-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Button 
          variant="outline" 
          onClick={() => navigate('/tgpsc/prelims')} 
          className="mb-4"
          data-testid="back-to-prelims"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to TGPSC Prelims Hub
        </Button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-pink-100 rounded-lg">
              <Dna className="w-8 h-8 text-pink-600" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
                PYQ Pattern Genome
              </h1>
              <p className="text-slate-600">TGPSC Topic Rotation Tracker</p>
            </div>
            <Badge className="bg-pink-500 ml-auto">TGPSC Group-1</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Analyzes 10 years of TGPSC Prelims patterns to identify topic rotation cycles. 
            Discover which topics are "due" for 2026 based on historical patterns.
          </p>
        </div>

        {/* Telangana-Specific Subjects */}
        {stateSpecificSubjects.length > 0 && (
          <Card className="mb-6 border-pink-100">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-pink-600" />
                Telangana-Specific Subjects
                <Badge variant="outline" className="ml-2 bg-pink-50 text-pink-600 border-pink-200">
                  State Focus
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingSubjects ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-pink-600" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {stateSpecificSubjects.map((subject) => (
                    <Button
                      key={subject.id}
                      variant={selectedSubject === subject.id ? "default" : "outline"}
                      className={`justify-start h-auto py-3 px-4 ${
                        selectedSubject === subject.id 
                          ? "bg-pink-600 hover:bg-pink-700" 
                          : "hover:bg-pink-50 hover:border-pink-300"
                      }`}
                      onClick={() => fetchGenomeAnalysis(subject.id)}
                      disabled={loading}
                      data-testid={`subject-${subject.id}`}
                    >
                      <div className="text-left">
                        <div className="font-medium text-sm">{subject.name}</div>
                        <div className={`text-xs ${selectedSubject === subject.id ? "text-pink-200" : "text-slate-500"}`}>
                          {subject.category || "State PSC"}
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* General Studies Subjects */}
        {generalSubjects.length > 0 && (
          <Card className="mb-6 border-blue-100">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                General Studies
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingSubjects ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {generalSubjects.map((subject) => (
                    <Button
                      key={subject.id}
                      variant={selectedSubject === subject.id ? "default" : "outline"}
                      className={`justify-start h-auto py-3 px-4 ${
                        selectedSubject === subject.id 
                          ? "bg-blue-600 hover:bg-blue-700" 
                          : "hover:bg-blue-50 hover:border-blue-300"
                      }`}
                      onClick={() => fetchGenomeAnalysis(subject.id)}
                      disabled={loading}
                      data-testid={`subject-${subject.id}`}
                    >
                      <div className="text-left">
                        <div className="font-medium text-sm">{subject.name}</div>
                        <div className={`text-xs ${selectedSubject === subject.id ? "text-blue-200" : "text-slate-500"}`}>
                          {subject.category || "General"}
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {loading && (
          <Card className="border-pink-100">
            <CardContent className="py-16">
              <div className="flex flex-col items-center justify-center gap-4">
                <div className="relative">
                  <Dna className="w-12 h-12 text-pink-600 animate-pulse" />
                  <Sparkles className="w-4 h-4 text-amber-500 absolute -top-1 -right-1 animate-bounce" />
                </div>
                <p className="text-slate-600 font-medium">Analyzing 10 years of TGPSC patterns...</p>
                <p className="text-xs text-slate-400">Identifying rotation cycles and correlations</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Genome Results */}
        {genomeData && !loading && (
          <div className="space-y-6">
            {/* Summary Header */}
            {genomeData.genome_summary && (
              <Card className="bg-gradient-to-r from-pink-500 to-rose-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold mb-1">
                        {genomeData.subject} - Pattern Analysis
                      </h3>
                      <p className="text-pink-100 text-sm">
                        {typeof genomeData.genome_summary === 'string' 
                          ? genomeData.genome_summary 
                          : `${genomeData.genome_summary?.total_topics_analyzed || 0} topics analyzed | ${genomeData.genome_summary?.hot_topics_count || 0} hot topics`}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold">{genomeData.high_probability_topics?.length || 0}</div>
                      <div className="text-xs text-pink-100">High Priority Topics</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* High Probability Topics */}
            {genomeData.high_probability_topics && genomeData.high_probability_topics.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Flame className="w-5 h-5 text-red-500" />
                  <h3 className="font-semibold text-slate-800">
                    High Probability Topics for TGPSC 2026
                  </h3>
                  <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50">
                    Must Prepare
                  </Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {genomeData.high_probability_topics.slice(0, isPro ? 20 : 5).map((topic, idx) => (
                    <TopicCard key={idx} topic={topic} type="high" />
                  ))}
                </div>
                
                {!isPro && genomeData.high_probability_topics.length > 5 && (
                  <Card className="mt-4 bg-amber-50 border-amber-200">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Lock className="w-5 h-5 text-amber-600" />
                        <span className="text-amber-800 font-medium">
                          {genomeData.high_probability_topics.length - 5} more high-priority topics available
                        </span>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-amber-500 hover:bg-amber-600"
                        onClick={() => navigate("/upgrade")}
                      >
                        Unlock PRO
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Recently Asked (Low Probability) */}
            {genomeData.recently_asked && genomeData.recently_asked.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <TrendingDown className="w-5 h-5 text-green-500" />
                  <h3 className="font-semibold text-slate-800">
                    Recently Asked Topics (Lower Priority)
                  </h3>
                  <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                    Quick Review
                  </Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {genomeData.recently_asked.slice(0, isPro ? 15 : 3).map((topic, idx) => (
                    <div key={idx} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-700 text-sm">{topic.topic}</span>
                        <Badge className="bg-green-500 text-white text-xs">
                          {topic.probability_2026}%
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Last asked: {topic.last_asked}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            <Card className="bg-slate-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between text-sm text-slate-600">
                  <div className="flex items-center gap-4">
                    <span>Exam: {genomeData.exam_name || "TGPSC Group-1"}</span>
                    <span>Subject: {genomeData.subject}</span>
                    <span>Analysis: {genomeData.analysis_period || "2015-2024"}</span>
                  </div>
                  <Badge variant="outline">
                    {genomeData.access_level || (isPro ? "PRO" : "FREE")} Access
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  How to Use PYQ Genome
                </h3>
                <ul className="text-sm text-slate-700 space-y-1">
                  <li>• <strong>Red (70%+)</strong>: Topics highly likely to appear - prioritize these</li>
                  <li>• <strong>Amber (50-70%)</strong>: Medium probability - include in revision</li>
                  <li>• <strong>Green (&lt;50%)</strong>: Recently asked - quick review sufficient</li>
                  <li>• Check cycle patterns to predict topic recurrence</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Empty State */}
        {!loading && !genomeData && !loadingSubjects && (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <Dna className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="font-medium text-slate-600 mb-2">Select a Subject</h3>
              <p className="text-sm text-slate-400">
                Choose a subject above to analyze its PYQ pattern and rotation cycle
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TGPSCPYQGenomePage;
